import React from 'react';
import { useDispatch } from 'react-redux';
import {
    Image,
    Filter,
    Crop,
    Palette,
    Layers,
    Zap,
    Trash2,
} from 'lucide-react';
import { ProcessorNode as ProcessorNodeType } from './utils/state/processingFlowSlice';
import {
    selectProcessorNode,
    removeProcessorNode,
    moveProcessorNode,
} from './utils/state/processingFlowSlice';

interface ProcessorNodeProps {
    node: ProcessorNodeType;
    isSelected: boolean;
    onStartConnection?: (nodeId: string) => void;
    onEndConnection?: (nodeId: string) => void;
    scale: number;
}

// Icon mapping for different processor types
const getProcessorIcon = (processorName: string) => {
    const name = processorName.toLowerCase();
    if (name.includes('resize') || name.includes('crop')) return Crop;
    if (name.includes('filter') || name.includes('blur')) return Filter;
    if (name.includes('overlay') || name.includes('composite')) return Layers;
    if (name.includes('enhance') || name.includes('sharpen')) return Zap;
    if (name.includes('color') || name.includes('palette')) return Palette;
    return Image; // Default icon
};

// Get category color for the node
const getCategoryColor = (processorName: string): string => {
    const name = processorName.toLowerCase();
    if (
        name.includes('resize') ||
        name.includes('crop') ||
        name.includes('scale')
    )
        return 'bg-green-500';
    if (
        name.includes('filter') ||
        name.includes('blur') ||
        name.includes('sharpen')
    )
        return 'bg-purple-500';
    if (
        name.includes('overlay') ||
        name.includes('composite') ||
        name.includes('blend')
    )
        return 'bg-orange-500';
    if (
        name.includes('color') ||
        name.includes('brightness') ||
        name.includes('contrast')
    )
        return 'bg-pink-500';
    if (name.includes('text') || name.includes('watermark'))
        return 'bg-indigo-500';
    return 'bg-blue-500'; // Default color
};

export default function ProcessorNode({
    node,
    isSelected,
    onStartConnection,
    onEndConnection,
    scale,
}: ProcessorNodeProps) {
    const dispatch = useDispatch();
    const IconComponent = getProcessorIcon(node.processor.name);
    const categoryColor = getCategoryColor(node.processor.name);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(selectProcessorNode(node.id));
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(removeProcessorNode(node.id));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return; // Only handle left mouse button

        const startX = e.clientX;
        const startY = e.clientY;
        const startNodeX = node.position.x;
        const startNodeY = node.position.y;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = (e.clientX - startX) / scale;
            const deltaY = (e.clientY - startY) / scale;

            dispatch(
                moveProcessorNode({
                    nodeId: node.id,
                    position: {
                        x: startNodeX + deltaX,
                        y: startNodeY + deltaY,
                    },
                }),
            );
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleConnectionStart = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onStartConnection) {
            onStartConnection(node.id);
        }
    };

    const handleConnectionEnd = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onEndConnection) {
            onEndConnection(node.id);
        }
    };

    return (
        <div
            className={`absolute bg-background rounded-lg shadow-lg border-2 transition-all cursor-move select-none ${
                isSelected
                    ? 'border-blue-500 shadow-xl'
                    : 'border-border hover:border-border hover:shadow-md'
            }`}
            style={{
                left: node.position.x,
                top: node.position.y,
                width: 200,
                minHeight: 120,
            }}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
        >
            {/* Header */}
            <div
                className={`${categoryColor} text-white p-3 rounded-t-lg flex items-center justify-between`}
                style={{ fontSize: '14px' }} // Explicit font size to ensure visibility
            >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <IconComponent className="w-4 h-4 flex-shrink-0" />
                    <span 
                        className="font-medium truncate"
                        style={{ 
                            color: 'white',
                            fontSize: '14px',
                            lineHeight: '1.2'
                        }}
                    >
                        {node.processor.title || node.processor.name}
                    </span>
                </div>
                <button
                    onClick={handleDelete}
                    className="text-white/80 hover:text-white transition-colors p-1 hover:bg-white/20 rounded"
                    title="Delete processor"
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>

            {/* Body */}
            <div className="p-3" style={{ fontSize: '12px' }}>
                {/* Description */}
                {node.processor.description && (
                    <p 
                        className="mb-3 line-clamp-2"
                        style={{ 
                            color: '#4b5563',
                            fontSize: '12px',
                            lineHeight: '1.4'
                        }}
                    >
                        {node.processor.description}
                    </p>
                )}

                {/* Arguments count */}
                <div className="flex items-center justify-between mb-2">
                    <span
                        style={{ 
                            color: '#6b7280',
                            fontSize: '12px'
                        }}
                    >
                        {
                            Object.keys(node.processor.arguments).filter(
                                (key) => key !== 'in' && key !== 'out',
                            ).length
                        }{' '}
                        arguments
                    </span>
                </div>

                {/* File info */}
                <div 
                    style={{ 
                        color: '#6b7280',
                        fontSize: '12px'
                    }}
                >
                    Files: {node.processor.fileCount} • Type:{' '}
                    {node.processor.inputType || 'any'}
                </div>
            </div>

            {/* Connection Points */}
            <div className="absolute -left-2 top-1/2 transform -translate-y-1/2">
                <div
                    className="w-4 h-4 bg-gray-400 rounded-full border-2 border-white shadow cursor-pointer hover:bg-gray-600 transition-colors"
                    onClick={handleConnectionEnd}
                    title="Input connection point"
                />
            </div>
            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2">
                <div
                    className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow cursor-pointer hover:bg-blue-600 transition-colors"
                    onClick={handleConnectionStart}
                    title="Output connection point"
                />
            </div>

            {/* Selection indicator */}
            {isSelected && (
                <div className="absolute -inset-1 border-2 border-blue-500 rounded-lg pointer-events-none animate-pulse" />
            )}
        </div>
    );
}
